import debounce from 'lodash/debounce';

import { IApiError } from '@frontend/request';

import { copyTradeAPIController } from '@aqx-core/common/api/copy-trade/controller';
import { IStrategy } from '@aqx-core/common/api/copy-trade/model';
import {
  ECopyTradeFilter,
  EPrecheckLoadingType,
  ESectionPreferenceStrategyType,
  PELICAN_COPY_TRADE_STRATEGY_LIST_CHUNK_SIZE,
  PELICAN_COPY_TRADE_STRATEGY_LIST_CHUNK_SIZE_FREE,
  PELICAN_COPY_TRADE_STRATEGY_SEARCH_DEBOUNCE
} from '@aqx-core/common/constants/copy-trade';
import { i18n } from '@aqx-core/common/i18n';
import { ECommonResponseCode, mapResponseCodeToMessage } from '@aqx-core/common/request';
import { companyService } from '@aqx-core/common/services/company';
import { copyTradeSectionSettingsViewController } from '@aqx-core/common/services/copy-trade/section-settings';
import { copyTradeSectionSettingsService } from '@aqx-core/common/services/copy-trade/section-settings/service';
import { copyTradeService } from '@aqx-core/common/services/copy-trade/service';
import { isSelectedCopier } from '@aqx-core/common/services/copy-trade/utils';
import { EUISnackBarMessageType, snackbarController } from '@aqx-core/common/services/snackbar';
import storage, { EStorageKey } from '@aqx-core/common/storage';
import { validateCompanyAccess } from '@aqx-core/common/utils/company-access';

import {
  ICreateCopySettingsParams,
  IEditCopierDrawdownParams,
  IEditCopySettingsParams,
  IGetCopierInfoParams,
  IGetStrategyDetailParams,
  IGetStrategyListParams,
  IGetUserInfoParams,
  ILinkAccountToPelicanCopyTradeParams,
  IOnStrategySectionSelectedParams,
  IPelicanCopyTradeAuthParams,
  IPrecheckStrategyDetailParams,
  IPrecheckUserInfoParams,
  IRemoveCopierParams,
  IResetCopierDrawdownParams,
  ISearchStrategyParams,
  IStopCopySettingsParams,
  ISwitchCopierParams
} from './types';

class CopyTradeViewController {
  private getChunkSize = (): number => {
    const companyProfile = companyService.context?.companyProfile;
    const { hasPremiumCopyTradeAccess } = validateCompanyAccess(companyProfile);
    return hasPremiumCopyTradeAccess
      ? PELICAN_COPY_TRADE_STRATEGY_LIST_CHUNK_SIZE
      : PELICAN_COPY_TRADE_STRATEGY_LIST_CHUNK_SIZE_FREE;
  };

  public setCodeVerifier = (codeVerifier: string) => {
    copyTradeService.setCodeVerifier(codeVerifier);
  };

  public pelicanCopyTradeAuth = async ({
    authorizationCode,
    codeVerifier,
    redirectUri,
    oauthClientId,
    onSuccess,
    onError
  }: IPelicanCopyTradeAuthParams) => {
    if (!oauthClientId) {
      onError?.(ECommonResponseCode.PW_1029);
      return;
    }

    copyTradeService.setAuthLoading(true);

    await copyTradeAPIController.auth(
      { authorizationCode, codeVerifier, redirectUri, oauthClientId },
      {
        onSuccess: (code) => {
          if (code === ECommonResponseCode.OK) {
            copyTradeService.setIsAuthenticated(true);
            onSuccess?.();
          }
          copyTradeService.setAuthLoading(false);
        },
        onError: (error) => {
          const { code } = error as IApiError;
          onError?.(code);
          copyTradeService.setAuthLoading(false);
        }
      }
    );
  };

  public getVerifierCode = () => {
    return copyTradeService.context.codeVerifier;
  };

  private autoSelectDefaultStrategy = (strategies: IStrategy[]) => {
    if (strategies.length > 0) {
      copyTradeService.setSelectedStrategy(strategies[0]);
      this.getStrategyTableData(ESectionPreferenceStrategyType.STRATEGY_PROFILE);
    } else {
      copyTradeService.setSelectedStrategy(null);
      copyTradeService.setSelectedStrategyDetail(null);
    }
  };

  public getStrategyList = async ({
    section,
    copierId,
    lastRank,
    chunkSize = this.getChunkSize(),
    search,
    loadMore = false,
    skipAutoSelect = false,
    signal,
    onSuccess,
    onError
  }: IGetStrategyListParams) => {
    if (loadMore) {
      copyTradeService.setStrategyListLoadingMore(true);
    } else {
      copyTradeService.setStrategyListLoading(true);
    }

    const copierIdToUse = copierId ?? copyTradeService.context.selectedCopierId ?? undefined;

    await copyTradeAPIController.getStrategyList(
      { section, copierId: copierIdToUse, lastRank, chunkSize, search },
      {
        onSuccess: (result) => {
          if (signal?.aborted) {
            return;
          }
          const newStrategies = result?.strategies ?? [];

          if (loadMore) {
            const existingStrategies = copyTradeService.context.strategyList;
            copyTradeService.setStrategyList([...existingStrategies, ...newStrategies]);
          } else {
            copyTradeService.setStrategyList(newStrategies);
            if (!skipAutoSelect) {
              this.autoSelectDefaultStrategy(newStrategies);
            }
          }

          const hasMore = newStrategies.length >= chunkSize;
          copyTradeService.setHasMoreStrategies(hasMore);

          this.updateDisplayStrategyList();
          onSuccess?.();
        },
        onError: (error) => {
          const { code } = error as IApiError;
          if (!loadMore) {
            copyTradeService.setStrategyList([]);
            copyTradeService.setHasMoreStrategies(false);
            this.updateDisplayStrategyList();
          }
          onError?.();
          if (code === ECommonResponseCode.PW_1003) {
            this.reset();
          }
        },
        onFinally: () => {
          if (loadMore) {
            copyTradeService.setStrategyListLoadingMore(false);
          }
          copyTradeService.setStrategyListLoading(false);
        }
      },
      signal
    );
  };

  public loadMoreStrategies = async () => {
    const currentList = copyTradeService.context.strategyList;
    const currentSection = copyTradeService.context.selectedStrategySection;
    const searchQuery = copyTradeService.context.searchQuery;
    const hasMore = copyTradeService.context.hasMoreStrategies;
    const selectedCopierId = copyTradeService.context.selectedCopierId;

    if (!hasMore || currentList.length === 0) {
      return;
    }

    const lastRank = currentList[currentList.length - 1]?.rank;
    await this.getStrategyList({
      section: currentSection,
      copierId: selectedCopierId,
      lastRank,
      search: searchQuery || undefined,
      loadMore: true,
      signal: copyTradeAPIController.getSectionSwitchSignal()
    });
  };

  private debouncedSearchStrategies = debounce(({ section, search }: ISearchStrategyParams) => {
    const selectedCopierId = copyTradeService.context.selectedCopierId;
    this.getStrategyList({
      section,
      copierId: selectedCopierId,
      search
    });
  }, PELICAN_COPY_TRADE_STRATEGY_SEARCH_DEBOUNCE);

  public onStrategySectionSelected = async ({ section, chunkSize }: IOnStrategySectionSelectedParams) => {
    copyTradeService.setSearchQuery('');
    let defaultSection = copyTradeSectionSettingsViewController.getFallbackSection();
    if (!section) {
      const copierInfo = copyTradeService.context.copierInfo;

      if (copierInfo?.id && copierInfo.isCopyingStrategies) {
        defaultSection = ECopyTradeFilter.EARLIER_COPIED;
      }
    }

    let selectedSection = section ?? defaultSection;

    // Validate selected section against available sections and apply fallback if needed
    const availableSections = copyTradeSectionSettingsService.context.availableStrategySections;
    if (availableSections.length > 0 && !availableSections.includes(selectedSection)) {
      selectedSection = copyTradeSectionSettingsViewController.getFallbackSection();
    }

    copyTradeService.setSelectedStrategySection(selectedSection);
    this.debouncedSearchStrategies.cancel();
    copyTradeService.setStrategyListLoading(true);
    copyTradeService.setHasMoreStrategies(true);
    copyTradeService.setStrategyList([]);
    copyTradeService.setSelectedStrategy(null);
    copyTradeService.setSelectedStrategyDetail(null);

    // Abort any in-flight section switch requests before starting new ones
    const signal = copyTradeAPIController.createSectionSwitchSignal();

    await this.getUserInfo({
      isCustomLoading: true,
      signal,
      onSuccess: () => {
        const selectedCopierId = copyTradeService.context.selectedCopierId;
        this.getStrategyList({
          section: selectedSection,
          copierId: selectedCopierId,
          chunkSize,
          signal,
          onSuccess: () => {
            this.onStrategyDetailTabChange(ESectionPreferenceStrategyType.STRATEGY_PROFILE);
            this.updateDisplayStrategyList();
          }
        });
      }
    });
  };

  private updateDisplayStrategyList = () => {
    const currentSection = copyTradeService.context.selectedStrategySection;
    let strategiesToDisplay: IStrategy[] = [];

    switch (currentSection) {
      case ECopyTradeFilter.ALL:
        strategiesToDisplay = copyTradeService.context.strategyList;
        break;
      default:
        strategiesToDisplay = copyTradeService.context.strategyList;
    }

    copyTradeService.setDisplayStrategyList(strategiesToDisplay);
  };

  public onSearchQueryChange = (searchQuery: string) => {
    copyTradeService.setSearchQuery(searchQuery);
    const currentSection = copyTradeService.context.selectedStrategySection;
    copyTradeService.setHasMoreStrategies(false);
    this.debouncedSearchStrategies({
      section: currentSection,
      search: searchQuery || undefined
    });
  };

  private getStrategyTableData = (tab: ESectionPreferenceStrategyType) => {
    const selectedStrategy = copyTradeService.context.selectedStrategy;
    const strategyId = copyTradeService.context.selectedStrategy?.id;

    if (!strategyId) {
      return;
    }

    const signal = copyTradeAPIController.createStrategyDetailSignal();

    switch (tab) {
      case ESectionPreferenceStrategyType.HISTORY:
        this.getCopyTradeHistory(strategyId);
        break;
      case ESectionPreferenceStrategyType.OPEN_POSITIONS:
        this.getCopyTradeOpenPositions(strategyId);
        break;
      default:
        this.getStrategyDetail({
          strategyId,
          copierId: copyTradeService.context.selectedCopierId,
          timeframe: copyTradeService.context.returnHistoryChartTimeframe,
          signal,
          onSuccess: () => {
            if (selectedStrategy) {
              this.getStrategyImage(selectedStrategy.id);
            }
          }
        });
        break;
    }
  };

  public onStrategyDetailTabChange = (tab: ESectionPreferenceStrategyType) => {
    copyTradeService.setSelectedStrategyDetailTab(tab);
    this.getStrategyTableData(tab);
  };

  public getStrategyDetail = async ({
    strategyId,
    copierId,
    timeframe = copyTradeService.context.returnHistoryChartTimeframe,
    isCustomLoading,
    signal,
    onSuccess,
    onError,
    onFinally
  }: IGetStrategyDetailParams) => {
    if (!isCustomLoading) {
      copyTradeService.setSelectedStrategyDetailLoading(true);
    }

    await copyTradeAPIController.getStrategyDetail(
      { strategyId, copierId, timeframe },
      {
        onSuccess: (result) => {
          copyTradeService.setSelectedStrategyDetail(result ?? null);
          onSuccess?.(result);
        },
        onError: (error) => {
          const { isAborted } = error as IApiError & { isAborted?: boolean };
          if (isAborted) {
            return;
          }
          const { code } = error as IApiError;
          if (code === ECommonResponseCode.PW_1003) {
            this.reset();
          }
          onError?.();
        },
        onFinally: () => {
          // Only reset loading if the request wasn't aborted
          if (!signal?.aborted) {
            onFinally?.();
            copyTradeService.setSelectedStrategyDetailLoading(false);
          }
        }
      },
      signal
    );
  };

  public onStrategySelected = async (strategy: IStrategy) => {
    copyTradeService.setSelectedStrategy(strategy);
    copyTradeService.setSelectedStrategyDetailTab(ESectionPreferenceStrategyType.STRATEGY_PROFILE);
    storage.setItem(EStorageKey.COPY_TRADE_SELECTED_STRATEGY_ID, strategy.id);

    // Clear previous data and show loading for the right panel
    copyTradeService.setSelectedStrategyDetailLoading(true);
    copyTradeService.setSelectedStrategyDetail(null);
    copyTradeService.setStrategyImage(undefined);
    this.getStrategyTableData(ESectionPreferenceStrategyType.STRATEGY_PROFILE);
  };

  public restoreSelectedStrategy = async () => {
    const strategyId = storage.getItem<string>(EStorageKey.COPY_TRADE_SELECTED_STRATEGY_ID);
    if (!strategyId || copyTradeService.context.selectedStrategy) {
      return;
    }

    // Fetch user info first to get copierId (needed to check if strategy is copied)
    await this.getUserInfo({
      isCustomLoading: true,
      onSuccess: () => {
        const copierId = copyTradeService.context.selectedCopierId;
        this.getStrategyDetail({
          strategyId,
          copierId,
          timeframe: copyTradeService.context.returnHistoryChartTimeframe,
          onSuccess: (result) => {
            if (result) {
              copyTradeService.setSelectedStrategy({
                id: result.id,
                name: result.name,
                rank: result.rank ?? 0,
                measure: result.measure ?? '',
                liveReturn: result.liveReturn ?? 0,
                numCopiers: result.numCopiers ?? 0,
                fee: result.performanceFee ?? 0,
                risk: result.risk,
                wins: result.wins ?? 0,
                losses: result.losses ?? 0,
                copied: result.copied ?? false,
                publishDate: result.publishDate ?? ''
              });
              this.getStrategyImage(strategyId);
            }
          }
        });
      }
    });
  };

  public setIsAuthenticated = (isAuthenticated: boolean) => {
    copyTradeService.setIsAuthenticated(isAuthenticated);
  };

  public getUserInfo = async ({ isCustomLoading, signal, onSuccess, onError, onFinally }: IGetUserInfoParams) => {
    const effectiveSignal = signal ?? copyTradeAPIController.createUserInfoSignal();

    if (!isCustomLoading) {
      copyTradeService.setUserInfoLoading(true);
    }
    await copyTradeAPIController.getUserInfo(
      {
        onSuccess: async (result) => {
          copyTradeService.setUserInfo(result ?? null);
          let selectedCopier;
          const selectedCopierId = storage.getItem(EStorageKey.COPY_TRADE_SELECTED_COPIER_ID_KEY);

          // First time: no stored ID yet — auto-select the copier with isSelected: true
          if (!selectedCopierId) {
            const defaultCopier = result?.linkedCopiers.find((c) => c.isSelected);
            if (defaultCopier) {
              storage.setItem(EStorageKey.COPY_TRADE_SELECTED_COPIER_ID_KEY, defaultCopier.id);
              selectedCopier = defaultCopier;
            }
          } else {
            selectedCopier = result?.linkedCopiers.find(isSelectedCopier);
          }

          copyTradeService.setSelectedCopierId(selectedCopier?.id);
          copyTradeService.setCopierInfo(selectedCopier ?? null);

          onSuccess?.(result);
        },
        onError: (error) => {
          const { code } = error as IApiError;
          onError?.();
          if (code === ECommonResponseCode.PW_1003) {
            this.reset();
          }
        },
        onFinally: () => {
          if (effectiveSignal.aborted) {
            return;
          }
          onFinally?.();
          copyTradeService.setUserInfoLoading(false);
        }
      },
      effectiveSignal
    );
  };

  private setPrecheckLoading = (type: EPrecheckLoadingType, isLoading: boolean) => {
    switch (type) {
      case EPrecheckLoadingType.COPY:
        copyTradeService.setIsPrecheckingCopyStrategy(isLoading);
        break;
      case EPrecheckLoadingType.EDIT:
        copyTradeService.setIsPrecheckingEditCopyStrategy(isLoading);
        break;
      case EPrecheckLoadingType.STOP:
        copyTradeService.setIsPrecheckingStopCopying(isLoading);
        break;
      case EPrecheckLoadingType.DRAWDOWN:
        copyTradeService.setIsPrecheckingDrawdown(isLoading);
        break;
      default:
        break;
    }
  };

  public precheckUserInfo = async ({ loadingType, onSuccess, onError }: IPrecheckUserInfoParams) => {
    this.setPrecheckLoading(loadingType, true);
    await this.getUserInfo({
      isCustomLoading: true,
      onSuccess: (userInfo) => {
        onSuccess?.(userInfo);
      },
      onError: () => {
        this.setPrecheckLoading(loadingType, false);
        onError?.();
      },
      onFinally: () => {
        this.setPrecheckLoading(loadingType, false);
      }
    });
  };

  public precheckStrategyDetail = async ({ loadingType, onSuccess, onError }: IPrecheckStrategyDetailParams) => {
    const strategyId = copyTradeService.context.selectedStrategy?.id;
    const copierId = copyTradeService.context.selectedCopierId;

    if (!strategyId) {
      return;
    }

    this.setPrecheckLoading(loadingType, true);

    await this.getUserInfo({
      isCustomLoading: true,
      onSuccess: (userInfo) => {
        if (!userInfo) {
          this.setPrecheckLoading(loadingType, false);
          return;
        }

        this.getStrategyDetail({
          strategyId,
          copierId,
          timeframe: copyTradeService.context.returnHistoryChartTimeframe,
          isCustomLoading: true,
          onSuccess: (strategyDetail) => {
            onSuccess?.(userInfo, strategyDetail);
          },
          onError: () => {
            onError?.();
          },
          onFinally: () => {
            this.setPrecheckLoading(loadingType, false);
          }
        });
      },
      onError: () => {
        this.setPrecheckLoading(loadingType, false);
        onError?.();
      }
    });
  };

  public setSelectedCopierId = (copierId?: string) => {
    copyTradeService.setSelectedCopierId(copierId);
  };

  public getStrategyImage = async (strategyId: string) => {
    copyTradeService.setStrategyImage(undefined);
    await copyTradeAPIController.getStrategyImage(
      { strategyId },
      {
        onSuccess: (result) => {
          copyTradeService.setStrategyImage(result ?? undefined);
        },
        onError: () => {}
      }
    );
  };

  public getCopyTradeOpenPositions = async (strategyId: string) => {
    copyTradeService.setIsLoadingCopyTradeOpenPositions(true);
    await copyTradeAPIController.getCopyTradeOpenPositions(
      { strategyId },
      {
        onSuccess: (result) => {
          copyTradeService.setCopyTradeOpenPositions(result ?? []);
          copyTradeService.setIsLoadingCopyTradeOpenPositions(false);
        },
        onError: () => {
          copyTradeService.setIsLoadingCopyTradeOpenPositions(false);
        }
      }
    );
  };

  public getCopyTradeHistory = async (strategyId: string) => {
    copyTradeService.setIsLoadingCopyTradeHistory(true);
    copyTradeService.setHasMoreCopyTradeHistory(true);
    const endDate = new Date().getTime();
    const startDate = endDate - 1000 * 60 * 60 * 24 * 30; // 30 days ago
    await copyTradeAPIController.getCopyTradeHistory(
      {
        strategyId,
        startDate: startDate,
        endDate: endDate
      },
      {
        onSuccess: (result) => {
          const history = result ?? [];
          copyTradeService.setCopyTradeHistory(history);
          // If we got less than expected, there might be no more data
          // For now, we'll assume there's more if we got a full page (we'll refine this based on API behavior)
          copyTradeService.setHasMoreCopyTradeHistory(history.length > 0);
          copyTradeService.setIsLoadingCopyTradeHistory(false);
        },
        onError: () => {
          copyTradeService.setIsLoadingCopyTradeHistory(false);
          copyTradeService.setHasMoreCopyTradeHistory(false);
        }
      }
    );
  };

  public loadMoreCopyTradeHistory = async (strategyId: string) => {
    const currentHistory = copyTradeService.context.copyTradeHistory;
    if (currentHistory.length === 0 || copyTradeService.context.isLoadingCopyTradeHistoryMore) {
      return;
    }

    copyTradeService.setIsLoadingCopyTradeHistoryMore(true);

    // Find the oldest closeTimestamp in current history
    const oldestTimestamp = Math.min(...currentHistory.map((item) => item.closeTimestamp));

    // Load 30 days before the oldest item
    const endDate = oldestTimestamp - 1000;
    const startDate = endDate - 1000 * 60 * 60 * 24 * 30; // 30 days before oldest

    await copyTradeAPIController.getCopyTradeHistory(
      {
        strategyId,
        startDate: startDate,
        endDate: endDate
      },
      {
        onSuccess: (result) => {
          const newHistory = result ?? [];
          // Append older history items to the end (assuming API returns newest first)
          const combinedHistory = [...currentHistory, ...newHistory];
          copyTradeService.setCopyTradeHistory(combinedHistory);
          // If we got no new data, assume no more data
          copyTradeService.setHasMoreCopyTradeHistory(newHistory.length > 0);
          copyTradeService.setIsLoadingCopyTradeHistoryMore(false);
        },
        onError: () => {
          copyTradeService.setIsLoadingCopyTradeHistoryMore(false);
          copyTradeService.setHasMoreCopyTradeHistory(false);
        }
      }
    );
  };

  // Master data controller
  public getBrokers = async () => {
    await copyTradeAPIController.getBrokers({
      onSuccess: (result) => {
        copyTradeService.setBrokers(result ?? null);
      },
      onError: () => {}
    });
  };

  public getServers = async () => {
    copyTradeService.setIsLoadingServers(true);
    await copyTradeAPIController.getServers({
      onSuccess: (result) => {
        copyTradeService.setServers(result ?? null);
        copyTradeService.setIsLoadingServers(false);
      },
      onError: () => {
        copyTradeService.setIsLoadingServers(false);
      }
    });
  };

  // Copier
  public linkAccountToPelicanCopyTrade = async ({
    values,
    onSuccess,
    onError
  }: ILinkAccountToPelicanCopyTradeParams) => {
    copyTradeService.setIsLoadingLinkAccountToPelicanCopyTrade(true);
    const { copyTradeMappingBroker } = copyTradeService.context.userInfo?.tenantCopyTradeConfig ?? {};
    await copyTradeAPIController.linkCopier(
      {
        brokerCode: copyTradeMappingBroker || '',
        serverCode: values.serverCode,
        username: values.username,
        password: values.password
      },
      {
        onSuccess: async (result) => {
          onSuccess?.(result);
          // refetch user info
          await this.getUserInfo({
            onSuccess: () => {
              this.handleSetCopierInfo();
            }
          });
          copyTradeService.setIsLoadingLinkAccountToPelicanCopyTrade(false);
        },
        onError: (error) => {
          const { code } = error as IApiError;
          onError?.(code);
          copyTradeService.setIsLoadingLinkAccountToPelicanCopyTrade(false);
        }
      }
    );
  };

  private handleSetCopierInfo = () => {
    const selectedCopier = copyTradeService.context.userInfo?.linkedCopiers.find(isSelectedCopier);
    copyTradeService.setSelectedCopierId(selectedCopier?.id);
    copyTradeService.setCopierInfo(selectedCopier ?? null);
  };

  public getCopierInfo = async ({ copierId, onSuccess, onError }: IGetCopierInfoParams) => {
    await copyTradeAPIController.getCopierInfo(
      { copierId },
      {
        onSuccess: (result) => {
          copyTradeService.setCopierInfo(result ?? null);
          onSuccess?.(result);
        },
        onError: () => {
          onError?.();
        }
      }
    );
  };

  public resetCopierDrawdown = async ({ copierId, onSuccess, onError }: IResetCopierDrawdownParams) => {
    copyTradeService.setIsLoadingResetCopierDrawdown(true);
    await copyTradeAPIController.resetCopierDrawdown(
      { copierId },
      {
        onSuccess: () => {
          onSuccess?.();
          this.getUserInfo({
            isCustomLoading: true,
            onSuccess: () => {
              this.handleSetCopierInfo();
            },
            onFinally: () => {
              copyTradeService.setIsLoadingResetCopierDrawdown(false);
            }
          });
        },
        onError: (error) => {
          const { code } = error as IApiError;
          const copierId = code === ECommonResponseCode.PW_1005 ? '' : copyTradeService.context.selectedCopierId;

          snackbarController.showAlert({
            message: mapResponseCodeToMessage(code as ECommonResponseCode) ?? i18n.t('Failed to edit copier drawdown'),
            messageType: EUISnackBarMessageType.ERROR
          });

          if (code === ECommonResponseCode.PW_1003) {
            this.reset();
          }
          if (code === ECommonResponseCode.PW_1005 || code === ECommonResponseCode.PW_1006) {
            this.getStrategyList({
              section: copyTradeService.context.selectedStrategySection,
              copierId
            });
          }
          onError?.(code);
          copyTradeService.setIsLoadingResetCopierDrawdown(false);
        }
      }
    );
  };

  public editCopierDrawdown = async ({ values, onSuccess, onError }: IEditCopierDrawdownParams) => {
    copyTradeService.setIsLoadingEditCopierDrawdown(true);
    await copyTradeAPIController.editCopierDrawdown(
      {
        copierId: values.copierId,
        softStopLevel: values.softStopLevel,
        hardStopLevel: values.hardStopLevel
      },
      {
        onSuccess: (result) => {
          onSuccess?.();
          snackbarController.showAlert({
            message: i18n.t('Edit Drawdown Successful.'),
            messageType: EUISnackBarMessageType.SUCCESS
          });
          // refetch copier info
          copyTradeService.setCopierInfo(result ?? null);
          this.getCopierInfo({ copierId: values.copierId });
          copyTradeService.setIsLoadingEditCopierDrawdown(false);
        },
        onError: async (error) => {
          const { code } = error as IApiError;
          if (code === ECommonResponseCode.PW_1003) {
            this.reset();
          }
          if (code === ECommonResponseCode.PW_1005 || code === ECommonResponseCode.PW_1006) {
            await this.getUserInfo({
              onSuccess: async () => {
                const strategyId = copyTradeService.context.selectedStrategyDetail?.id;
                if (strategyId) {
                  await this.refetchStrategyListAndDetailsAfterCopySettings(strategyId);
                }
              }
            });
          }

          snackbarController.showAlert({
            message: mapResponseCodeToMessage(code as ECommonResponseCode, i18n.t('Failed to Edit Drawdown.')),
            description: i18n.t('Please try again.'),
            messageType: EUISnackBarMessageType.ERROR
          });

          onError?.(code);
          copyTradeService.setIsLoadingEditCopierDrawdown(false);
        }
      }
    );
  };

  public unlinkCopier = async ({ copierId, onSuccess, onError }: IRemoveCopierParams) => {
    copyTradeService.setIsLoadingRemoveCopier(true);
    await copyTradeAPIController.unlinkCopier(
      { copierId },
      {
        onSuccess: () => {
          onSuccess?.();
          copyTradeService.setCopierInfo(null);
          copyTradeService.setIsLoadingRemoveCopier(false);
        },
        onError: (error) => {
          const { code } = error as IApiError;
          onError?.(code);
          copyTradeService.setIsLoadingRemoveCopier(false);
        }
      }
    );
  };

  public switchCopier = async ({ copierId, onSuccess, onError }: ISwitchCopierParams) => {
    copyTradeService.setIsLoadingSwitchCopier(true);
    await copyTradeAPIController.switchCopierAccount(copierId, {
      onSuccess: async () => {
        onSuccess?.();
        await this.getUserInfo({
          onSuccess: () => {
            this.handleSetCopierInfo();
          }
        });
        copyTradeService.setIsLoadingSwitchCopier(false);
      },
      onError: (error) => {
        const { code } = error as IApiError;
        copyTradeService.setCopierInfo(null);
        copyTradeService.setSelectedCopierId(undefined);
        onError?.(code);
        copyTradeService.setIsLoadingSwitchCopier(false);
      }
    });
  };

  public setShouldShowLinkAccountToPelicanCopyTradeModal = (shouldShow: boolean) => {
    copyTradeService.setShouldShowLinkPelicanAccountModal(shouldShow);
  };

  // Copy settings
  public getCopySettings = async (copierId: string, strategyId: string) => {
    copyTradeService.setIsLoadingCopySettings(true);
    await copyTradeAPIController.getCopySettings(
      { copierId, strategyId },
      {
        onSuccess: (result) => {
          copyTradeService.setCopySettings(result ?? null);
          copyTradeService.setIsLoadingCopySettings(false);
        },
        onError: () => {
          copyTradeService.setIsLoadingCopySettings(false);
        }
      }
    );
  };

  public editCopySettings = async ({ values, onSuccess, onError }: IEditCopySettingsParams) => {
    copyTradeService.setIsLoadingEditCopySettings(true);
    await copyTradeAPIController.editCopySettings(
      {
        copierId: values.copierId,
        strategyId: values.strategyId,
        tradeSizeType: values.tradeSizeType,
        tradeSizeValue: values.tradeSizeValue,
        isOpenExistingTrades: values.isOpenExistingTrades,
        isRoundUpToMinimumSize: values.isRoundUpToMinimumSize
      },
      {
        onSuccess: () => {
          onSuccess?.();
          copyTradeService.setIsLoadingEditCopySettings(false);
        },
        onError: async (error) => {
          const { code } = error as IApiError;
          onError?.(code);
          if (code === ECommonResponseCode.PW_1003) {
            snackbarController.showAlert({
              message: mapResponseCodeToMessage(code as ECommonResponseCode),
              messageType: EUISnackBarMessageType.ERROR
            });
            this.reset();
          }
          if (code === ECommonResponseCode.PW_1005 || code === ECommonResponseCode.PW_1006) {
            await this.getUserInfo({
              onSuccess: async () => {
                await this.refetchStrategyListAndDetailsAfterCopySettings(values.strategyId);
              }
            });
          }

          copyTradeService.setIsLoadingEditCopySettings(false);
        }
      }
    );
  };

  public createCopySettings = async ({ values, onSuccess, onError }: ICreateCopySettingsParams) => {
    copyTradeService.setIsLoadingCreateCopySettings(true);
    await copyTradeAPIController.createCopySettings(
      {
        copierId: values.copierId,
        strategyId: values.strategyId,
        tradeSizeType: values.tradeSizeType,
        tradeSizeValue: values.tradeSizeValue,
        isOpenExistingTrades: values.isOpenExistingTrades,
        isRoundUpToMinimumSize: values.isRoundUpToMinimumSize
      },
      {
        onSuccess: () => {
          onSuccess?.();
          // refetch strategy detail
          this.getStrategyDetail({
            strategyId: values.strategyId,
            copierId: values.copierId,
            timeframe: copyTradeService.context.returnHistoryChartTimeframe,
            onSuccess: () => {
              this.getStrategyImage(values.strategyId);
            }
          });
          copyTradeService.setIsLoadingCreateCopySettings(false);
        },
        onError: async (error) => {
          const { code } = error as IApiError;
          onError?.(code);
          if (code === ECommonResponseCode.PW_1003) {
            snackbarController.showAlert({
              message: mapResponseCodeToMessage(code as ECommonResponseCode),
              messageType: EUISnackBarMessageType.ERROR
            });
            this.reset();
          }
          if (code === ECommonResponseCode.PW_1005) {
            await this.getUserInfo({
              onSuccess: async () => {
                await this.refetchStrategyListAndDetailsAfterCopySettings(values.strategyId);
              }
            });
          }
          if (code === ECommonResponseCode.PW_1007) {
            await this.getStrategyDetail({
              strategyId: values.strategyId,
              copierId: copyTradeService.context.selectedCopierId,
              onSuccess: async () => {
                await this.getStrategyImage(values.strategyId);
              }
            });
          }
          copyTradeService.setIsLoadingCreateCopySettings(false);
        }
      }
    );
  };

  public refetchStrategyListAndDetailsAfterCopySettings = async (
    strategyId = copyTradeService.context.selectedStrategyDetail?.id
  ) => {
    if (
      copyTradeService.context.selectedStrategySection === ECopyTradeFilter.EARLIER_COPIED &&
      copyTradeService.context.strategyList.length === 1
    ) {
      const fallbackSection = copyTradeSectionSettingsViewController.getFallbackSection();
      this.onStrategySectionSelected({ section: fallbackSection });
    } else {
      // refetch strategy list without auto-selecting a different strategy
      this.getStrategyList({
        section: copyTradeService.context.selectedStrategySection,
        copierId: copyTradeService.context.selectedCopierId,
        skipAutoSelect: true
      });
    }

    // refetch strategy detail
    if (strategyId) {
      this.getStrategyDetail({
        strategyId,
        copierId: copyTradeService.context.selectedCopierId,
        timeframe: copyTradeService.context.returnHistoryChartTimeframe,
        onSuccess: () => {
          this.getStrategyImage(strategyId);
        }
      });
    }
  };

  public stopCopySettings = async ({ values, onSuccess, onError }: IStopCopySettingsParams) => {
    copyTradeService.setIsLoadingStopCopySettings(true);
    await copyTradeAPIController.stopCopySettings(
      { copierId: values.copierId, strategyId: values.strategyId, mode: values.mode },
      {
        onSuccess: async () => {
          onSuccess?.();
          await this.refetchStrategyListAndDetailsAfterCopySettings(values.strategyId);
          copyTradeService.setIsLoadingStopCopySettings(false);
        },
        onError: async (error) => {
          const { code } = error as IApiError;
          onError?.(code);
          if (code === ECommonResponseCode.PW_1003) {
            this.reset();
          }
          if (code === ECommonResponseCode.PW_1005 || code === ECommonResponseCode.PW_1006) {
            await this.getUserInfo({
              onSuccess: async () => {
                await this.refetchStrategyListAndDetailsAfterCopySettings(values.strategyId);
              }
            });
          }
          copyTradeService.setIsLoadingStopCopySettings(false);
        }
      }
    );
  };

  public resetCopySettings = () => {
    copyTradeService.resetCopySettings();
  };

  public setReturnHistoryChartExpanded = (isExpanded: boolean) => {
    copyTradeService.setIsReturnHistoryChartExpanded(isExpanded);
  };

  public reset = () => {
    this.debouncedSearchStrategies.cancel();
    copyTradeAPIController.abortSectionSwitch();
    copyTradeService.reset();
  };
}

export const copyTradeViewController = new CopyTradeViewController();
